/**
 * Returns `raw` as a same-origin path (pathname + search), or undefined if it
 * would leave the origin. Use it on any `next`-style param before handing it to
 * location.assign: an unvalidated value is an open redirect launched from a
 * genuine OnFrontiers URL. Prefer this over `getSameOriginPath` in
 * `@/utils`, which accepts only absolute URLs and has none of the hardening below.
 *
 * Resolved through the URL parser rather than matched as a string, because
 * location.assign uses the parser and the two disagree. "/\evil.com" leaves the
 * origin via the parser's relative-slash state, and tab, CR and LF are stripped
 * before parsing, so "/<CR>/evil.com" becomes protocol-relative after any
 * startsWith('//') check has already passed.
 */
export function sameOriginPath(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return undefined;
    const path = `${url.pathname}${url.search}`;
    // The origin check above is not sufficient on its own. `/..//evil.com`
    // resolves same-origin, but the parser normalises it to the *path*
    // "//evil.com", and handing that back to location.assign re-parses it as
    // protocol-relative. Same for "/.//", "/%2e%2e//" and "/foo/..//".
    if (!path.startsWith('/') || path.startsWith('//')) return undefined;
    return path;
  } catch {
    return undefined;
  }
}
